// 顶栏登录状态组件 / Top-bar login / user-dropdown component.
import { authApi as defaultAuthApi, type LanguageTag } from "@/api/auth";
import { startButtonLoading } from "@/features/async-button";
import { openAuthModal } from "@/features/auth-modal";
import { clearCachedAuthStatus, writeCachedAuthStatus } from "@/features/auth-session-cache";
import { openChangePasswordModal } from "@/features/change-password-modal";
import { openMcpGuideModal } from "@/features/mcp-guide-modal";
import { state } from "@/state/store";

interface Deps {
  authApi: Pick<typeof defaultAuthApi, "logout" | "updatePreferences">;
  redirect: (url: string) => void;
}
const DEFAULT_DEPS: Deps = {
  authApi: defaultAuthApi,
  redirect: (url) => { window.location.href = url; },
};
let headerAuthEvents: AbortController | null = null;

export function renderHeaderAuth(deps: Partial<Deps> = {}): void {
  const d = { ...DEFAULT_DEPS, ...deps };
  const root = document.getElementById("header-auth");
  if (!root) return;
  headerAuthEvents?.abort();
  headerAuthEvents = new AbortController();
  const { signal } = headerAuthEvents;
  if (!state.auth?.authenticated) {
    _renderLoginButton(root, signal);
    return;
  }
  const user = state.auth.user;
  if (!user) {
    _renderLoginButton(root, signal);
    return;
  }
  const customerId = user.customer_id ?? "";
  const mcpDisabled = customerId ? "" : " disabled aria-disabled=\"true\"";
  const language = normalizeLanguage(user.preferred_language);
  root.innerHTML = `
    <div class="language-toggle" role="group" aria-label="Language">
      <button
        class="${language === "en" ? "active" : ""}"
        type="button"
        data-language="en"
        aria-pressed="${language === "en"}"
      >EN</button>
      <button
        class="${language === "cn" ? "active" : ""}"
        type="button"
        data-language="cn"
        aria-pressed="${language === "cn"}"
      >CN</button>
    </div>
    <button
      class="button ghost mcp-guide-btn"
      type="button"
      title="Connect your agent"
      aria-label="Connect your agent"${mcpDisabled}
    >
      <svg aria-hidden="true" viewBox="0 0 24 24" width="17" height="17" fill="none"
        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M9 7v4" />
        <path d="M15 7v4" />
        <path d="M7 11h10" />
        <path d="M8 11v3a4 4 0 0 0 8 0v-3" />
        <path d="M12 18v3" />
      </svg>
    </button>
    <div class="user-dropdown">
      <button class="button ghost user-btn" type="button" aria-haspopup="menu" aria-expanded="false">
        <span class="user-email">${user.email}</span>
        <span class="user-credit">Credit: ${user.credit}</span>
        <span class="user-caret" aria-hidden="true">▾</span>
      </button>
      <div class="menu" role="menu" hidden>
        <button class="menu-change-pwd" type="button" role="menuitem">Change password</button>
        <button class="menu-logout" type="button" role="menuitem">Log out</button>
      </div>
    </div>
  `;
  root.querySelectorAll<HTMLButtonElement>("[data-language]").forEach((button) => {
    button.addEventListener("click", (event) => {
      event.stopPropagation();
      void switchLanguage(button, d, deps);
    }, { signal });
  });
  root.querySelector<HTMLButtonElement>(".mcp-guide-btn")!
    .addEventListener("click", (event) => {
      event.stopPropagation();
      if (!customerId) return;
      openMcpGuideModal({ token: customerId });
    }, { signal });
  const userButton = root.querySelector<HTMLButtonElement>(".user-btn")!;
  userButton.addEventListener("click", () => {
    const menu = root.querySelector<HTMLElement>(".menu")!;
    setUserMenuOpen(root, menu.hidden);
  }, { signal });
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Node && root.contains(target)) return;
    setUserMenuOpen(root, false);
  }, { signal });
  root.querySelector<HTMLButtonElement>(".menu-change-pwd")!
    .addEventListener("click", () => {
      setUserMenuOpen(root, false);
      openChangePasswordModal();
    }, { signal });
  root.querySelector<HTMLButtonElement>(".menu-logout")!
    .addEventListener("click", async (event) => {
      const button = event.currentTarget as HTMLButtonElement;
      if (button.disabled) return;
      const restore = startButtonLoading(button, "Logging out");
      try {
        await d.authApi.logout();
      } catch {
        // 本地退出优先：服务端 logout 失败时仍清理本地会话展示。
        // Local logout wins: clear local session UI even if server logout fails.
      } finally {
        restore();
      }
      clearCachedAuthStatus();
      state.auth = {
        authenticated: false,
        provider: "local",
        user: null,
        capabilities: {
          register: true,
          password_reset: true,
          change_password: false,
          logout: false,
        },
      };
      renderHeaderAuth(deps);
      d.redirect("/");
    }, { signal });
}

// 渲染匿名态登录入口 / Render the anonymous login entrypoint.
function _renderLoginButton(root: HTMLElement, signal: AbortSignal): void {
  root.innerHTML = `<button class="button ghost login-btn" type="button">Sign in</button>`;
  root.querySelector<HTMLButtonElement>(".login-btn")!.addEventListener(
    "click", () => openAuthModal("login"),
    { signal },
  );
}

// 设置用户下拉菜单开关 / Toggle the user dropdown state.
function setUserMenuOpen(root: HTMLElement, open: boolean): void {
  const menu = root.querySelector<HTMLElement>(".menu");
  const button = root.querySelector<HTMLButtonElement>(".user-btn");
  if (!menu || !button) return;
  menu.hidden = !open;
  button.setAttribute("aria-expanded", String(open));
}

// 切换账号语言 / Switch the account language.
async function switchLanguage(
  button: HTMLButtonElement,
  deps: ResolvedDeps,
  renderDeps: Partial<Deps>,
): Promise<void> {
  const next = normalizeLanguage(button.dataset.language);
  if (state.auth?.user?.preferred_language === next) return;
  const root = document.getElementById("header-auth");
  root?.querySelectorAll<HTMLButtonElement>("[data-language]").forEach((item) => {
    item.disabled = true;
  });
  const restore = startButtonLoading(button, next.toUpperCase());
  try {
    const response = await deps.authApi.updatePreferences({ preferred_language: next });
    if (state.auth?.authenticated && response.user) {
      state.auth = { ...state.auth, user: response.user };
      writeCachedAuthStatus(state.auth);
      window.dispatchEvent(new CustomEvent("ripple:language-changed", {
        detail: { language: next },
      }));
      window.dispatchEvent(new CustomEvent("ripple:auth-updated"));
    }
  } finally {
    restore();
    renderHeaderAuth(renderDeps);
  }
}

type ResolvedDeps = typeof DEFAULT_DEPS;

// 规范化语言值 / Normalize language values.
function normalizeLanguage(value: unknown): LanguageTag {
  return value === "en" ? "en" : "cn";
}
